/**
 * Animation Design Tokens
 *
 * Animation-specific token handling that uses the general token utilities.
 * This matches the SCSS core/animations/_tokens.scss implementation.
 */

import { createTokenExtractor } from '../core/tokens';
import { type AnimationDurationName, type AnimationTimingName } from '../values/animations';

/**
 * Animation duration token extractor with type safety
 *
 * Matches SCSS: duration($name)
 *
 * @param name - The name of the duration token (must be a valid AnimationDurationName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the duration token
 */
export const duration = createTokenExtractor<AnimationDurationName>('animation-duration');

/**
 * Animation timing-function token extractor with type safety
 *
 * Matches SCSS: timing($name)
 *
 * @param name - The name of the timing token (must be a valid AnimationTimingName)
 * @param fallback - Optional fallback value
 * @returns CSS var() function for the timing token
 */
export const timing = createTokenExtractor<AnimationTimingName>('animation-timing');

/**
 * Example usage:
 *
 * ```typescript
 * import { duration, timing } from './animations';
 *
 * const transition = `opacity ${duration('fast')} ${timing('smooth')}`;
 *
 * // TypeScript will show error for invalid names:
 * // const invalid = duration('quick'); // ❌ Type error
 * ```
 */
